<div class="page-container">
  <h2>Terms of Service</h2>
  <div class="tos-date"><strong>Last Updated:</strong> December 14, 2024</div>

  <div class="terms-container">
    <h2>1. Agreement to Terms</h2>
    <p>By accessing our website at <a href="https://verticaltalentgroup.com">verticaltalentgroup.com</a> (the "Site") or using our mobile application (the "App"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you must not access or use the Site or App.</p>

    <h2>2. Scope of Terms</h2>
    <p>These Terms of Service are divided into two sections:</p>
    <ul>
        <li><strong>Website Terms:</strong> Apply to all visitors who access the public pages of our Site.</li>
        <li><strong>Mobile App Terms:</strong> Apply to creators represented by Vertical Talent who use our iPhone App.</li>
    </ul>

    <h2>3. Website Terms</h2>
    <p>The following terms apply to all visitors of our public website:</p>

    <h3>3.1 Permitted Use</h3>
    <p>You may access and browse our Site for informational purposes. You may also submit information through our Contact Us form to inquire about our services.</p>

    <h3>3.2 Prohibited Activities</h3>
    <p>When using our Site, you agree not to:</p>
    <ul>
        <li>Attempt to gain unauthorized access to any portion of the Site or any systems or networks connected to the Site.</li>
        <li>Use any automated means (such as bots, scrapers, or crawlers) to access the Site without our express written permission.</li>
        <li>Interfere with or disrupt the Site or servers or networks connected to the Site.</li>
        <li>Transmit any viruses, malware, or other malicious code.</li>
        <li>Violate any applicable laws or regulations.</li>
        <li>Collect or harvest any personally identifiable information from the Site.</li>
    </ul>

    <h3>3.3 Intellectual Property</h3>
    <p>All content on the Site, including but not limited to text, graphics, logos, images, and software, is the property of Vertical Talent, LLC and is protected by United States and international copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content on the Site without our express written permission.</p>

    <h3>3.4 Disclaimer of Warranties</h3>
    <p>The Site and all information provided on the Site are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the Site will be uninterrupted, error-free, or free of viruses or other harmful components.</p>

    <h3>3.5 Limitation of Liability</h3>
    <p>To the fullest extent permitted by law, Vertical Talent, LLC shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of or inability to use the Site, including but not limited to damages for loss of profits, data, or other intangible losses.</p>

    <h3>3.6 External Links</h3>
    <p>Our Site may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of any third-party websites. You access third-party websites at your own risk.</p>

    <h2>4. Mobile App Terms</h2>
    <p>The following terms apply to creators represented by Vertical Talent who use our iPhone App:</p>

    <h3>4.1 Eligibility and Access</h3>
    <p>The App is available exclusively to creators who are represented by Vertical Talent, LLC and who have been provided with a &#64;verticaltalent.co email account. You must be at least 18 years of age to use the App. By using the App, you confirm that you meet these eligibility requirements.</p>

    <h3>4.2 Account Security</h3>
    <p>You are responsible for maintaining the confidentiality of your &#64;verticaltalent.co account credentials and for all activities that occur under your account. You agree to:</p>
    <ul>
        <li>Keep your login credentials secure and not share them with others.</li>
        <li>Notify us immediately if you suspect any unauthorized use of your account.</li>
        <li>Use strong passwords and enable any available security features.</li>
    </ul>

    <h3>4.3 Acceptable Use of the App</h3>
    <p>When using the App, you agree to:</p>
    <ul>
        <li>Use the App only for its intended purposes (viewing campaign information, uploading deliverables, and communicating about campaigns).</li>
        <li>Not upload any content that is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable.</li>
        <li>Not upload any viruses, malware, or other malicious code.</li>
        <li>Not attempt to reverse engineer, decompile, or disassemble the App.</li>
        <li>Not use the App in any manner that could damage, disable, overburden, or impair our servers or networks.</li>
        <li>Comply with all applicable laws and regulations when using the App.</li>
    </ul>

    <h3>4.4 Content You Upload</h3>
    <p>You are solely responsible for any content you upload to the App, including video assets, images, and other deliverables. By uploading content to the App, you represent and warrant that:</p>
    <ul>
        <li>You own the content or have the necessary rights and permissions to upload it.</li>
        <li>The content does not infringe on any third party's intellectual property, privacy, or other rights.</li>
        <li>You grant Vertical Talent permission to share the uploaded content with brand partners as specified in your representation agreement or campaign contracts.</li>
    </ul>
    <p>Ownership and licensing rights for content you create are governed by your representation agreement and individual campaign contracts, not by these Terms of Service.</p>

    <h3>4.5 App Permissions</h3>
    <p>The App may request the following permissions on your device:</p>
    <ul>
        <li><strong>Push Notifications:</strong> To alert you about campaign updates, messages, and important deadlines. You can disable push notifications in your device settings at any time.</li>
        <li><strong>Photo Library Access:</strong> To enable you to select and upload video assets and deliverables from your device. This permission is requested only when you choose to upload content and is not used for any other purpose.</li>
    </ul>
    <p>We do not access or use these permissions for any purpose other than the functionality described above.</p>

    <h3>4.6 App Availability and Updates</h3>
    <p>We strive to make the App available at all times, but we do not guarantee that the App will be uninterrupted or error-free. We may update, modify, or discontinue the App or any features at any time without notice. We may also perform maintenance that temporarily restricts access to the App.</p>

    <h3>4.7 Suspension and Termination</h3>
    <p>We reserve the right to suspend or terminate your access to the App at any time, with or without notice, for any reason, including but not limited to:</p>
    <ul>
        <li>Violation of these Terms of Service.</li>
        <li>Uploading prohibited or malicious content.</li>
        <li>Attempting to compromise the security or integrity of the App.</li>
        <li>Termination of your representation agreement with Vertical Talent.</li>
    </ul>
    <p>Upon termination, your access to the App will be revoked, and your &#64;verticaltalent.co account may be deactivated. Campaign records and work history will be retained as described in our Privacy Policy.</p>

    <h3>4.8 Limitation of Liability for App Use</h3>
    <p>The App is provided "as is" and "as available" without warranties of any kind. To the fullest extent permitted by law, Vertical Talent, LLC shall not be liable for any damages arising from your use of the App, including but not limited to:</p>
    <ul>
        <li>Loss of data or content.</li>
        <li>Interruptions or errors in App functionality.</li>
        <li>Unauthorized access to your account resulting from your failure to secure your credentials.</li>
        <li>Any disputes arising from campaign contracts or business relationships.</li>
    </ul>

    <h2>5. Privacy</h2>
    <p>Your use of the Site and App is also governed by our Privacy Policy, which can be found at <a href="https://verticaltalentgroup.com/privacy">verticaltalentgroup.com/privacy</a>. By using the Site or App, you consent to the collection and use of your information as described in the Privacy Policy.</p>

    <h2>6. Changes to These Terms</h2>
    <p>We reserve the right to update or modify these Terms of Service at any time without prior notice. When we make changes, we will update the "Last Updated" date at the top of this page. Your continued use of the Site or App after any changes constitutes your acceptance of the updated Terms of Service. We encourage you to review these terms periodically.</p>

    <h2>7. Governing Law and Jurisdiction</h2>
    <p>These Terms of Service are governed by and construed in accordance with the laws of the State of Idaho and the United States of America, without regard to conflict of law principles. Any disputes arising from these terms or your use of the Site or App shall be resolved in the state or federal courts located in Idaho, and you consent to the exclusive jurisdiction of such courts.</p>

    <h2>8. Severability</h2>
    <p>If any provision of these Terms of Service is found to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions shall remain in full force and effect.</p>

    <h2>9. Entire Agreement</h2>
    <p>These Terms of Service, together with our Privacy Policy, constitute the entire agreement between you and Vertical Talent, LLC regarding your use of the Site and App, and supersede any prior agreements or understandings.</p>

    <h2>10. Contact Us</h2>
    <p>If you have questions or concerns about these Terms of Service, please contact us at:</p>
    <p>
        <strong>Vertical Talent, LLC</strong><br>
        Email: <a href="mailto:info@verticaltalentgroup.com">info&#64;verticaltalentgroup.com</a>
    </p>
  </div>
</div>
